import React, { useState, useEffect } from "react";
import { cn } from "../../utils/cn";

// ─── Avatar de contacto (foto de perfil con fallback a inicial) ──────────────
//
// Para DMs (WhatsApp, etc.) intenta pintar la foto de perfil real del contacto
// (`contact_profile_pic`). Si no hay foto, o la URL falla/expira, degrada al
// avatar de inicial con color de tono — nunca muestra una imagen rota.
//
// Las URLs de WhatsApp (pps.whatsapp.net) tienen protección de hotlink y
// caducan con el tiempo: `referrerPolicy="no-referrer"` evita el bloqueo por
// referer y `onError` cubre la expiración cayendo a la inicial. El backend
// refresca la URL en el próximo inbound del contacto.
//
// Grupos y canal IA NO usan foto: el llamador sigue pintando sus íconos
// (Users / Bot) como antes; este componente cubre solo la persona.
export default function ContactAvatar({
  src,
  initial,
  toneClass,
  className,
  imgClassName,
  // Cuando hay foto: click sobre la imagen la amplía (estilo WhatsApp Web). El
  // handler frena la propagación para no disparar la acción del contenedor
  // (p. ej. abrir el panel de contacto desde el header). Sobre la inicial no
  // hay nada que ampliar, así que no aplica.
  onImageClick,
}) {
  const [broken, setBroken] = useState(false);

  // Una foto nueva (cambio de conversación o refresh de URL) reintenta cargar:
  // sin esto, un onError previo dejaría el fallback pegado al cambiar de src.
  useEffect(() => {
    setBroken(false);
  }, [src]);

  const showImg = !!src && !broken;
  const clickable = showImg && !!onImageClick;

  return (
    <span
      className={cn(
        "rounded-full flex items-center justify-center font-semibold overflow-hidden",
        showImg ? "bg-bg-muted" : toneClass,
        // WhatsApp-style hover ring: cuando la imagen es clickeable, un anillo
        // sutil aparece al hover para indicar que se puede ampliar. Sobre fondo
        // claro (header) usa brand; sobre fondo oscuro (lightbox) hereda.
        clickable &&
          "ring-2 ring-transparent hover:ring-brand-300 transition-[box-shadow,transform] hover:scale-105 active:scale-95",
        className,
      )}
      aria-hidden="true"
    >
      {showImg ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          onClick={
            clickable
              ? (e) => {
                  e.stopPropagation();
                  onImageClick();
                }
              : undefined
          }
          className={cn(
            // dark-safe-img: el invert global de dark mode volvía la foto un
            // disco blanco (gotcha documentado en DESIGN.md).
            "w-full h-full object-cover dark-safe-img",
            clickable && "cursor-zoom-in",
            imgClassName,
          )}
        />
      ) : (
        initial
      )}
    </span>
  );
}
